import { VisibilityOutlined } from '@mui/icons-material';
import { CardContainer, DataTable, Dropdown, SearchBar, Tag } from 'finara-components';
import type { DataTableColumn, DropdownOption } from 'finara-components';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState, CsvExportButton } from '@/components/common';
import { useOnboardingList } from '@/hooks/queries/useOnboarding';
import type { OnboardingItem, OnboardingStatus } from '@/types/onboarding';

export const OnboardingListTab: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<OnboardingStatus | ''>('');

  // Fetch onboarding list
  const {
    data: onboardingData,
    isLoading,
    error,
  } = useOnboardingList({
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    limit: 50,
    offset: 0,
  });

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'Todos los estados' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_revision', label: 'En Revisión' },
    { value: 'completado', label: 'Completado' },
  ];

  const getStatusVariant = (status: OnboardingStatus): 'blue' | 'gray' | 'yellow' | 'green' => {
    switch (status) {
      case 'en_proceso':
        return 'blue';
      case 'pendiente':
        return 'gray';
      case 'en_revision':
        return 'yellow';
      case 'completado':
        return 'green';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: OnboardingStatus): string => {
    switch (status) {
      case 'en_proceso':
        return 'En Proceso';
      case 'pendiente':
        return 'Pendiente';
      case 'en_revision':
        return 'En Revisión';
      case 'completado':
        return 'Completado';
      default:
        return status;
    }
  };

  const getPriorityVariant = (priority: 'alta' | 'media' | 'baja'): 'red' | 'yellow' | 'green' => {
    switch (priority) {
      case 'alta':
        return 'red';
      case 'media':
        return 'yellow';
      case 'baja':
        return 'green';
      default:
        return 'yellow';
    }
  };

  const getPriorityLabel = (priority: 'alta' | 'media' | 'baja'): string => {
    switch (priority) {
      case 'alta':
        return 'Alta';
      case 'media':
        return 'Media';
      case 'baja':
        return 'Baja';
      default:
        return priority;
    }
  };

  // Transform data to add top-level id required by DataTable
  const tableData =
    onboardingData?.data.map(item => ({
      ...item,
      id: item.employee.id,
    })) || [];

  const columns: DataTableColumn<OnboardingItem & { id: string }>[] = [
    {
      key: 'employee',
      label: 'Empleado',
      render: (_, item) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
            <span className="text-sm font-semibold text-muted">
              {item.employee.name
                .split(' ')
                .map(n => n[0])
                .join('')
                .substring(0, 2)}
            </span>
          </div>
          <div>
            <div className="font-medium text-brand-white">{item.employee.name}</div>
            <div className="text-sm text-muted">{item.employee.email}</div>
            <div className="text-xs text-muted">{item.employee.code}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'position',
      label: 'Puesto',
      render: (_, item) => (
        <div>
          <p className="text-sm text-brand-white">{item.jobTitle}</p>
          <p className="text-xs text-muted">{item.department}</p>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, item) => (
        <Tag variant={getStatusVariant(item.status)}>{getStatusLabel(item.status)}</Tag>
      ),
    },
    {
      key: 'progress',
      label: 'Progreso',
      render: (_, item) => (
        <div className="w-32">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex-1 bg-white/10 rounded-full h-2">
              <div
                className="bg-brand-blue rounded-full h-2"
                style={{ width: `${item.progress}%` }}
              ></div>
            </div>
            <span className="text-xs font-semibold text-muted">{item.progress}%</span>
          </div>
        </div>
      ),
    },
    {
      key: 'currentStep',
      label: 'Paso Actual',
      render: (_, item) => <span className="text-sm text-brand-white">{item.currentStep.title}</span>,
    },
    {
      key: 'priority',
      label: 'Prioridad',
      render: (_, item) => (
        <Tag variant={getPriorityVariant(item.priority)}>{getPriorityLabel(item.priority)}</Tag>
      ),
    },
    {
      key: 'assignedTo',
      label: 'Asignado a',
      render: (_, item) => (
        <span className="text-sm text-brand-white">{item.assignedTo || 'Sin asignar'}</span>
      ),
    },
    {
      key: 'daysInProcess',
      label: 'Días en Proceso',
      render: (_, item) => <span className="text-sm text-brand-white">{item.daysInProcess} días</span>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: (_, item) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => void navigate(`/empleados/${item.employee.id}`)}
            className="p-2 text-muted hover:text-brand-white hover:bg-white/5 rounded"
            title="Ver detalles"
          >
            <VisibilityOutlined fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) return <LoadingState message="Cargando lista de onboardings..." />;
  if (error)
    return <ErrorState title="Error al cargar la lista" message="Intenta nuevamente más tarde" />;

  return (
    <CardContainer>
      <div className="p-6">
        <div className="flex items-center gap-4 mb-6">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Buscar por nombre, email o ID..."
            className="flex-1"
          />
          <Dropdown
            id="status-filter"
            value={statusFilter}
            onChange={value => setStatusFilter(value as OnboardingStatus | '')}
            options={statusOptions}
            placeholder="Todos los estados"
          />
          <CsvExportButton
            data={tableData}
            filename="onboarding_empleados"
            columns={item => ({
              Empleado: item.employee.name,
              Email: item.employee.email,
              Código: item.employee.code,
              Puesto: item.jobTitle,
              Departamento: item.department,
              Estado: getStatusLabel(item.status),
              Progreso: `${item.progress}%`,
              'Paso Actual': item.currentStep.title,
              Prioridad: getPriorityLabel(item.priority),
              'Asignado a': item.assignedTo || 'Sin asignar',
              'Días en Proceso': item.daysInProcess,
            })}
            variant="white"
            size="small"
            buttonText="Exportar"
          />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-brand-white mb-4">
            Empleados en Onboarding ({onboardingData?.total || 0})
          </h3>
          {tableData.length > 0 ? (
            <div className="overflow-x-auto">
              <DataTable columns={columns} data={tableData} />
            </div>
          ) : (
            <EmptyState
              title="No hay empleados en onboarding"
              description="No se encontraron empleados con los filtros seleccionados"
            />
          )}
        </div>
      </div>
    </CardContainer>
  );
};
